import { useMemo, useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { FiCheck } from "react-icons/fi";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import axiosInstance from "api/axiosInstance";
import { showToast, confirmFreePlan } from "toast/toastHelper";
import { useLocation } from "react-router-dom";

const colors = {
    completed: {
        bg: "bg-gradient-to-br from-emerald-400 to-emerald-600",
        border: "border-emerald-400",
        glow: "shadow-emerald-500/30",
        text: "text-emerald-400",
    },
    active: {
        bg: "bg-gradient-to-br from-[#f7b400] via-[#fa9a2d] to-[#e8851a]",
        border: "border-[#f7b400]",
        glow: "shadow-[#f7b400]/40",
        text: "text-[#f7b400]",
    },
    pending: {
        bg: "bg-gradient-to-br from-zinc-400 to-zinc-600",
        border: "border-zinc-400",
        glow: "shadow-zinc-500/20",
        text: "text-zinc-400",
    },
    error: {
        bg: "bg-gradient-to-br from-red-400 to-red-600",
        border: "border-red-400",
        glow: "shadow-red-500/30",
        text: "text-red-400",
    },
};

export default function FloatingStepper() {
    const [open, setOpen] = useState(true);
    const [tooltipIndex, setTooltipIndex] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [steps, setSteps] = useState([]);
    const [position, setPosition] = useState("left");
    const [x, setX] = useState(0);
    const [dragX, setDragX] = useState(0);
    const [tooltipSide, setTooltipSide] = useState("right");
    const stepRefs = useRef([]);
    const isDragging = useRef(false);
    const activeTooltipRef = useRef(null);
    const tooltipRef = useRef(null);
    const location = useLocation();

    // در کامپوننت FloatingStepper
    // در FloatingStepper.js
    useEffect(() => {
        const getRoadmap = async () => {
            try {
                const res = await axiosInstance.get('/business/@me/roadmap');
                if (res?.data?.ok) {
                    setSteps(res?.data?.data.nodes);
                    if (res.data.data.freePlanMessage != null) {
                        // پاس دادن پیام از بک
                        confirmFreePlan(
                            res.data.data.freePlanMessage, // متن پیام
                            () => {
                                // کاری که بعد از تایید انجام بشه
                                console.log('Free plan confirmed');
                                // مثلاً رفرش کردن دیتا
                                // getRoadmap();
                            }
                        );
                    }
                }
            } catch (err) {
                console.error('Error fetching bots:', err);
            }
        };

        getRoadmap();
    }, [location.pathname]);
    useEffect(() => {
        const handleClickOutside = () => {
            setTooltipIndex(null);
        };

        document.addEventListener("click", handleClickOutside);

        return () => {
            document.removeEventListener("click", handleClickOutside);
        };
    }, []);
    useEffect(() => {
        if (tooltipIndex !== null) {
            updateTooltipPosition();

            window.addEventListener(
                "resize",
                updateTooltipPosition
            );

            window.addEventListener(
                "scroll",
                updateTooltipPosition,
                true
            );
        }

        return () => {
            window.removeEventListener(
                "resize",
                updateTooltipPosition
            );

            window.removeEventListener(
                "scroll",
                updateTooltipPosition,
                true
            );
        };
    }, [tooltipIndex, position]);

    const updateTooltipPosition = () => {
        if (!activeTooltipRef.current) return;

        const rect = activeTooltipRef.current.getBoundingClientRect();

        const tooltipWidth = tooltipRef.current?.getBoundingClientRect().width || 250;

        const gap = 15;

        const spaceRight = window.innerWidth - rect.right;

        if (spaceRight > tooltipWidth) {

            // tooltip سمت راست نود
            setTooltipSide("right");

            setTooltipPosition({
                top: rect.top + rect.height / 2,
                left: rect.right + gap
            });

        } else {

            // tooltip سمت چپ نود
            setTooltipSide("left");

            setTooltipPosition({
                top: rect.top + rect.height / 2,
                left: rect.left - tooltipWidth - gap
            });
        }
    };

    const currentStep = useMemo(() => {
        return (
            steps.find((step) => step.status !== "completed") || {
                status: "completed",
                title: "منتشر شده",
            }
        );
    }, [steps]);

    return (
        <motion.div
            className={`
        fixed bottom-6 z-[9990] w-20
        flex flex-col items-center
        ${position === "right" ? "right-6" : "left-6"}
    `}
            drag="x"
            dragElastic={0.1}
            dragMomentum={false}
            animate={{ x: dragX }}
            onDrag={(e, info) => {
                setDragX(info.offset.x);
            }}
            onDragEnd={(e, info) => {
                if (info.point.x < window.innerWidth / 2) {
                    setPosition("left");
                } else {
                    setPosition("right");
                }

                setDragX(0);
                isDragging.current = false;
            }}
        >
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1, x: x }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="flex flex-col-reverse items-center"
                    >
                        {/* کانتینر با اسکرول */}
                        <div
                            className="flex flex-col-reverse items-center bg-transparent
                                max-h-[60vh] overflow-y-auto 
                                px-3 pt-2 rounded-xl
                                scrollbar-hide
                                [&::-webkit-scrollbar-thumb]:rounded-full
                                [&::-webkit-scrollbar-track]:bg-transparent
                                [&::-webkit-scrollbar]:hidden

                            "
                        >
                            {steps.slice().map((step, index) => (
                                <div
                                    key={index}
                                    className="relative group flex items-center"
                                >
                                    <motion.div
                                        initial={{ opacity: 0, y: 20, scale: 0.8 }}
                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                        exit={{ opacity: 0, y: 20, scale: 0.8 }}
                                        transition={{ duration: 0.25, delay: index * 0.06 }}
                                        className="flex flex-col items-center"
                                    >
                                        {/* Node */}
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();

                                                activeTooltipRef.current = stepRefs.current[index];

                                                setTooltipIndex((prev) =>
                                                    prev === index ? null : index
                                                );

                                                setTimeout(() => {
                                                    updateTooltipPosition();
                                                }, 0);
                                            }}
                                            className="relative"
                                            ref={el => stepRefs.current[index] = el}
                                        >
                                            <div className={`
                                                absolute -inset-1 rounded-full 
                                                bg-gradient-to-r from-transparent via-white/20 to-transparent
                                                blur-md transition-all duration-300
                                                ${colors[step.status].glow}
                                                group-hover:scale-110 group-hover:blur-xl
                                            `} />

                                            <div className={`
                                                relative flex h-[60px] w-[60px] items-center justify-center
                                                rounded-full
                                                bg-gradient-to-br ${step.status === 'completed' ? 'from-emerald-400 to-emerald-600' :
                                                    step.status === 'active' ? 'from-[#f7b400] via-[#fa9a2d] to-[#e8851a]' :
                                                        step.status === 'pending' ? 'from-zinc-400 to-zinc-600 ' :
                                                            'from-red-400 to-red-600'
                                                }
                                                border-2 ${colors[step.status].border}
                                                transition-all duration-300
                                                group-hover:scale-110 
                                                group-hover:rotate-3
                                                cursor-pointer
                                            `}>
                                                <div className="absolute inset-0 rounded-full bg-gradient-to-t from-white/10 to-transparent" />

                                                <div className="relative z-10 text-center">

                                                    <div className="text-2xl">{step.icon}</div>

                                                    <p className="text-[12px] font-medium text-white/90 mt-0.5">
                                                        {step.title}
                                                    </p>
                                                </div>

                                                {step.status === 'completed' && (
                                                    <div className="absolute -top-1 -right-1 bg-emerald-400 rounded-full p-0.5 border-2 border-white shadow-lg">
                                                        <FiCheck className="w-3 h-3 text-white" />
                                                    </div>
                                                )}

                                                {step.status === 'active' && (
                                                    <div className="absolute -top-1 -right-1">
                                                        <span className="flex h-3 w-3">
                                                            <span className="absolute inline-flex h-full w-full rounded-full bg-[#f7b400] opacity-75 animate-ping" />
                                                            <span className="relative inline-flex rounded-full h-3 w-3 bg-[#f7b400] border-2 border-white" />
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {/* line */}
                                        {index !== steps.length && (
                                            <div className={`
                                                h-5 w-0.5 relative
                                                ${step.status === "completed" ? "bg-emerald-400" : "bg-zinc-400"}
                                                transition-all duration-300
                                                group-hover:h-8
                                            `}>
                                                <span className={`absolute top-90 -right-[0.150rem] mt-0.5 -translate-y-1/2 
                                                    w-0 h-0 rotate-90
                                                    border-t-[6px] border-t-transparent
                                                    border-b-[6px] border-b-transparent
                                                    border-r-[6px] ${step.status === "completed" ? "border-emerald-400" : "border-zinc-400"}`}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-white/20 to-transparent" />
                                            </div>
                                        )}
                                    </motion.div>
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Tooltip با موقعیت داینامیک */}
            {open && tooltipIndex !== null && steps[tooltipIndex] && (
                <div
                    ref={tooltipRef}
                    className="fixed z-[99999]"
                    style={{
                        top: tooltipPosition.top,
                        left: tooltipPosition.left,
                        transform: "translateY(-50%)"
                    }}
                >
                    <div className="whitespace-normal break-words rounded-xl 
                        bg-gradient-to-br from-[#1f3e39] to-[#2a4f48] 
                        px-4 py-3 text-xs text-white
                        max-w-[350px] min-w-[200px] 
                        shadow-2xl border border-white/10
                        leading-relaxed
                        backdrop-blur-sm
                        relative
                        animate-in fade-in duration-200
                    ">
                        {steps[tooltipIndex].description}
                        <br />
                        <span className="text-red-400">
                            {steps[tooltipIndex].specialMessage}
                        </span>
                        {steps[tooltipIndex]?.meta?.botUrl && (
                            <a
                                href={steps[tooltipIndex].meta.botUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-yellow-400 block mt-1 hover:underline break-all"
                            >
                                {steps[tooltipIndex].meta.botUrl}
                            </a>
                        )}
                        <span
                            className={`
                            absolute top-1/2 -translate-y-1/2
                            h-3 w-3 rotate-45
                            bg-[#1f3e39]
                            ${tooltipSide === "right"
                                    ? "-left-2"
                                    : "-right-2"
                                }
                         `}
                        />
                    </div>
                </div>
            )}

            {/* دکمه شناور */}
            <div className="relative group flex">
                <div className={`
                    absolute -inset-2 rounded-full 
                    bg-gradient-to-r from-transparent via-white/20 to-transparent
                    blur-2xl transition-all duration-500
                    ${colors[currentStep.status].glow}
                    group-hover:scale-150 group-hover:blur-3xl
                `} />

                <motion.button
                    whileHover={{ scale: 1.15, rotate: 3 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => {
                        if (!isDragging.current) {
                            setOpen((prev) => !prev);
                        }
                    }}
                    className={`
                        relative flex h-[72px] w-[72px] items-center justify-center
                        rounded-full shadow-2xl text-white
                        bg-gradient-to-br ${currentStep.status === 'completed' ? 'from-emerald-400 to-emerald-600' :
                            currentStep.status === 'active' ? 'from-[#f7b400] via-[#fa9a2d] to-[#e8851a]' :
                                'from-zinc-400 to-zinc-600'
                        }
                        border-2 ${colors[currentStep.status].border}
                        transition-all duration-300
                        hover:shadow-3xl hover:shadow-[#f7b400]/30
                    `}
                >
                    <div className="absolute inset-0 rounded-full bg-gradient-to-t from-white/10 to-transparent" />

                    <div className="relative z-10 text-center">
                        <div className="text-sm font-bold">
                            نقشه راه
                        </div>
                    </div>

                    {currentStep.status === 'active' && (
                        <div className="absolute -inset-1 rounded-full border-2 border-[#f7b400]/50 animate-spin-slow">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 
                                w-2 h-2 bg-[#f7b400] rounded-full shadow-lg shadow-[#f7b400]/50" />
                        </div>
                    )}
                </motion.button>
            </div>
        </motion.div>
    );
}